import { z } from 'zod';

/**
 * O contrato do TOOLKIT (F3 da revisão de out/2026, ADR-0026): o manifesto de
 * uma rotina, o resultado normalizado que ela devolve, a entrada de cada
 * ferramenta das sessões e o canal `toolkit.*` entre o daemon e a API.
 *
 * Mora no protocolo porque cruza o fio: o daemon executa o que a API guarda
 * (D17). O `shared` importa daqui — nunca o contrário.
 *
 * **Nenhuma forma de ferramenta usa `z.record`** (`agente.md`: registro MCP é
 * tudo-ou-nada, e um `record` na forma faz o servidor expor ZERO ferramentas,
 * calado). Lista de coisas é array de objetos, nunca mapa nem texto codificado
 * — o `--doc "arq=TIPO|Desc"` da JusAPI vira `[{arquivo, tipo, descricao}]`.
 *
 * Campos novos entram opcionais; nada aqui sobe `VERSAO_PROTOCOLO`.
 */

/** A classe de efeito de uma fase (D30). A ordem é a do risco. */
export const CLASSES_DE_EFEITO = ['LEITURA', 'ESCRITA_INTERNA', 'ATO_EXTERNO'] as const;
export const ClasseDeEfeitoSchema = z.enum(CLASSES_DE_EFEITO);
export type ClasseDeEfeito = z.infer<typeof ClasseDeEfeitoSchema>;

/**
 * Os tetos do toolkit. Acima de qualquer um, RECUSA — nunca trunca: um JSON
 * cortado vira lixo, e um pacote cortado roda outra coisa.
 */
export const LIMITES_TOOLKIT = {
  /** Um arquivo do pacote (texto). */
  bytesPorArquivo: 256 * 1024,
  /** O pacote inteiro de uma versão. */
  bytesPorVersao: 2 * 1024 * 1024,
  arquivosPorVersao: 64,
  fasesPorRotina: 20,
  /**
   * O teto da casa para o `timeoutSeg` de uma fase (R10): os envios reais da
   * JusAPI levam de 2 a 8 min e a revisão de prazos, 900 s por leitor.
   */
  timeoutMaximoSeg: 3_600,
  /** A última linha JSON que a rotina escreve no stdout (o resultado). */
  bytesDoResultado: 256 * 1024,
  descricao: 20_000,
  motivo: 2_000,
  consulta: 200,
} as const;

/**
 * Nome de rotina, de alvo, de fase, de segredo: minúsculas, dígitos e hífen,
 * começando e terminando em letra ou dígito. É o que as sessões digitam.
 */
export const SlugToolkitSchema = z
  .string()
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/, 'use minusculas, digitos e hifen (ex.: djen-consultar)');

/**
 * Caminho de um arquivo DENTRO do pacote: relativo, com `/`, sem `..`, sem
 * letra de unidade, sem barra inicial. O daemon ainda confere depois do
 * `realpath` (`agente.md`: allowlist); isto é a primeira barreira.
 */
export const CaminhoDoPacoteSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9_.-]+(?:\/[A-Za-z0-9_.-]+)*$/, 'caminho relativo com /, sem espacos')
  .refine((c) => !c.split('/').some((p) => p === '..' || p === '.'), 'sem . nem .. no caminho');

/**
 * Nome de PARÂMETRO de fase: um identificador (`numeroProcesso`, `uf_oab`), e
 * não um slug — é o nome que o script da rotina lê, e hífen não é nome de
 * variável em linguagem nenhuma. (A 1ª versão exigia slug, e a guarda das
 * rotinas-fonte recusou o manifesto do DJEN na primeira rodada.)
 */
export const NomeDeParametroSchema = z
  .string()
  .regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'use letras, digitos e _ (ex.: numeroProcesso)');

/** Nome de variável de ambiente por onde um segredo chega ao processo. */
const VariavelSchema = z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/, 'variavel em MAIUSCULAS (ex.: PDPJ_SENHA)');

/**
 * Um requisito TIPADO da rotina (R7), que o daemon confere ANTES de rodar —
 * o que falta vira resultado `PAROU`, com o nome do requisito.
 *
 * O segredo é só o NOME no cofre do Remoto (D33), e chega ao processo por
 * ambiente ou stdin, NUNCA pelo argv — a JusAPI põe o TOTP no argv do
 * `curl.exe`, que é a classe de defeito da F1.4.
 */
export const RequisitoSchema = z.discriminatedUnion('tipo', [
  z.object({
    tipo: z.literal('segredo'),
    nome: SlugToolkitSchema,
    variavel: VariavelSchema,
    entrega: z.enum(['ambiente', 'stdin']).default('ambiente'),
  }),
  z.object({ tipo: z.literal('certificado'), nome: z.string().min(1).max(200) }),
  z.object({
    tipo: z.literal('servico_local'),
    /** Só loopback: rotina não depende de serviço de outra máquina sem dizer. */
    endereco: z.string().regex(/^(127\.0\.0\.1|localhost):\d{1,5}$/, 'so 127.0.0.1:<porta> ou localhost:<porta>'),
  }),
  z.object({
    tipo: z.literal('navegador'),
    porta: z.number().int().min(1024).max(65_535),
    perfil: z.string().max(200).optional(),
  }),
  z.object({ tipo: z.literal('extensao'), nome: z.string().min(1).max(200) }),
  z.object({ tipo: z.literal('programa'), nome: z.string().min(1).max(200) }),
  z.object({ tipo: z.literal('projeto_vizinho'), caminho: z.string().min(1).max(400) }),
]);
export type RequisitoRotina = z.infer<typeof RequisitoSchema>;

/**
 * Um parâmetro de fase. Lista TIPADA, e não JSON Schema livre: dá para validar
 * sem biblioteca a mais, e não tem `record` (o plano pedia "JSON Schema, sem
 * `z.record`" — esta é a forma que cumpre as duas metades).
 */
export const ParametroDeFaseSchema = z.object({
  nome: NomeDeParametroSchema,
  tipo: z.enum(['texto', 'numero', 'booleano', 'lista_texto', 'data']),
  obrigatorio: z.boolean().default(true),
  descricao: z.string().max(500).default(''),
  /** Valores aceitos, quando o parâmetro é uma escolha. */
  opcoes: z.array(z.string().max(200)).max(50).optional(),
});
export type ParametroDeFase = z.infer<typeof ParametroDeFaseSchema>;

/**
 * Uma regra do critério de sucesso (R23): o que o `dados` do resultado PRECISA
 * trazer para a fase ser `CONFERIDO`. É parte da versão e vale igual em todos
 * os alvos dela — o que muda por caso é parâmetro, nunca edição.
 */
export const RegraDeSucessoSchema = z.object({
  /** Caminho com ponto dentro de `dados` (`prazos.fechado`). */
  campo: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*$/),
  regra: z.enum(['presente', 'nao_vazio', 'verdadeiro', 'igual']),
  valor: z.union([z.string(), z.number(), z.boolean()]).optional(),
});
export type RegraDeSucesso = z.infer<typeof RegraDeSucessoSchema>;

/** Uma fase da rotina: ensaio, enviar, conferir, sonda… cada uma com a sua classe. */
export const FaseDaRotinaSchema = z
  .object({
    nome: SlugToolkitSchema,
    classe: ClasseDeEfeitoSchema,
    timeoutSeg: z.number().int().min(1).max(LIMITES_TOOLKIT.timeoutMaximoSeg),
    parametros: z.array(ParametroDeFaseSchema).max(30).default([]),
    criterioDeSucesso: z.array(RegraDeSucessoSchema).min(1).max(30),
    /**
     * A partir de onde a fase NÃO se mata (R9): o marcador que a rotina escreve
     * no stdout ao passar do ponto. Só faz sentido num ato.
     */
    pontoSemRetorno: z.string().min(1).max(100).optional(),
  })
  .refine((f) => f.nome !== 'sonda' || f.classe === 'LEITURA', {
    message: 'a fase `sonda` e obrigatoriamente LEITURA (D32)',
    path: ['classe'],
  })
  .refine((f) => f.pontoSemRetorno === undefined || f.classe === 'ATO_EXTERNO', {
    message: 'ponto sem retorno so existe em ato externo',
    path: ['pontoSemRetorno'],
  });
export type FaseDaRotina = z.infer<typeof FaseDaRotinaSchema>;

/**
 * O MANIFESTO de uma versão (R7, R8, R10, R13, R14, R23). Faz parte do hash.
 *
 * `onde` (D23): a VPS roda uma rotina só se ela DECLARAR — e lá valem as
 * regras do `vps.md`. O que prende uma rotina a uma máquina específica não é
 * o `onde`, são os requisitos (Chrome na 9222, certificado, PJeOffice).
 */
export const ManifestoRotinaSchema = z
  .object({
    versaoDoManifesto: z.literal(1),
    /** O programa que roda a entrada (`python`, `node`, `pwsh`…) — D22: qualquer comando. */
    interpretador: z.string().min(1).max(100),
    argumentosDoInterpretador: z.array(z.string().max(200)).max(20).default([]),
    entrada: CaminhoDoPacoteSchema,
    onde: z
      .object({ pc: z.boolean(), vps: z.boolean().default(false) })
      .refine((o) => o.pc || o.vps, 'a rotina tem de rodar em algum lugar'),
    dependencias: z
      .array(z.object({ rotina: SlugToolkitSchema, versaoMinima: z.number().int().min(1) }))
      .max(20)
      .default([]),
    requisitos: z.array(RequisitoSchema).max(30).default([]),
    fases: z.array(FaseDaRotinaSchema).min(1).max(LIMITES_TOOLKIT.fasesPorRotina),
    /**
     * Os recursos que a rotina usa em exclusão mútua (R12): `navegador:9222`,
     * `cofre-cookies:<familia>`, `pjeoffice`, `word`. O `alvo:<chave>` entra
     * sozinho em toda execução: "nunca enxame contra tribunal" é código.
     */
    recursos: z
      .array(z.string().regex(/^[a-z0-9-]+(?::[A-Za-z0-9._-]+)?$/))
      .max(20)
      .default([]),
    /** O intervalo mínimo entre duas execuções no MESMO alvo (o ritmo). */
    ritmoPorAlvoSeg: z.number().int().min(0).max(86_400).default(0),
    /** De quanto em quanto tempo a sonda roda, se houver fase `sonda` (teto: uma por dia). */
    sondaACadaHoras: z.number().int().min(24).max(24 * 30).optional(),
  })
  .refine((m) => new Set(m.fases.map((f) => f.nome)).size === m.fases.length, {
    message: 'duas fases com o mesmo nome',
    path: ['fases'],
  })
  .refine((m) => m.sondaACadaHoras === undefined || m.fases.some((f) => f.nome === 'sonda'), {
    message: 'frequencia de sonda sem fase `sonda`',
    path: ['sondaACadaHoras'],
  });
export type ManifestoRotina = z.infer<typeof ManifestoRotinaSchema>;

/** Um arquivo do pacote, como a proposta o traz. O hash é do SERVIDOR. */
export const ArquivoPropostoSchema = z.object({
  caminho: CaminhoDoPacoteSchema,
  conteudo: z.string().max(LIMITES_TOOLKIT.bytesPorArquivo),
});
export type ArquivoProposto = z.infer<typeof ArquivoPropostoSchema>;

/** Os resultados normalizados (R14, R24): "falhou" ≠ "não consegui conferir". */
export const RESULTADOS_DE_ROTINA = ['CONFERIDO', 'FALHOU', 'NAO_CONFERIDO', 'PENDENCIA_HUMANA', 'PAROU'] as const;
export const ResultadoDeRotinaSchema = z.enum(RESULTADOS_DE_ROTINA);

/**
 * O que a ROTINA escreve como última linha JSON do stdout. O daemon lê, confere
 * contra este schema e o critério de sucesso, e acrescenta o que é dele (a
 * versão, o alvo, a fase) — a rotina não se autodeclara versão nenhuma.
 *
 * Código de saída sozinho não é resultado.
 */
export const SaidaDaRotinaSchema = z
  .object({
    resultado: ResultadoDeRotinaSchema,
    motivo: z.string().max(LIMITES_TOOLKIT.motivo),
    /** De quem é a falha (R16): obrigatório em `FALHOU`, proibido fora dele. */
    falhaDe: z.enum(['ALVO', 'AMBIENTE', 'ROTINA']).optional(),
    dados: z.unknown().optional(),
    artefatos: z
      .array(
        z.object({
          nome: z.string().min(1).max(200),
          sha256: z.string().regex(/^[0-9a-f]{64}$/),
          bytes: z.number().int().min(0),
        }),
      )
      .max(50)
      .default([]),
    /** O que a rotina espera de um humano (R11): o código do 2º fator, por exemplo. */
    pendencia: z
      .object({
        pergunta: z.string().min(1).max(500),
        /** Resposta redigida antes de virar linha de banco (`seguranca.md`, `isSecret`). */
        segredo: z.boolean(),
      })
      .optional(),
    /** De onde veio e quando (R21) — `incompleta` quando uma fonte faltou. */
    fonte: z
      .object({ descricao: z.string().max(500), em: z.string().max(40), incompleta: z.boolean().default(false) })
      .optional(),
  })
  .refine((s) => (s.resultado === 'FALHOU') === (s.falhaDe !== undefined), {
    message: 'falhaDe e obrigatorio em FALHOU e proibido fora dele',
    path: ['falhaDe'],
  })
  .refine((s) => (s.resultado === 'PENDENCIA_HUMANA') === (s.pendencia !== undefined), {
    message: 'pendencia e obrigatoria em PENDENCIA_HUMANA e proibida fora dela',
    path: ['pendencia'],
  });
export type SaidaDaRotina = z.infer<typeof SaidaDaRotinaSchema>;

/* ------------------------------------------------------------------ ferramentas */

/** Um valor de parâmetro, como a sessão o manda. */
const ValorDeParametroSchema = z.union([z.string().max(4_000), z.number(), z.boolean(), z.array(z.string().max(500)).max(100)]);

/** `toolkit_buscar`: leitura, aprovação automática nos três motores. */
export const ToolkitBuscarEntradaSchema = z.object({
  consulta: z.string().min(1).max(LIMITES_TOOLKIT.consulta),
  especie: z.enum(['rotina', 'verbete', 'alvo']).optional(),
  alvo: SlugToolkitSchema.optional(),
});

/** `toolkit_ler`: a vigente, o estado no alvo e o histórico resumido. */
export const ToolkitLerEntradaSchema = z.object({
  nome: SlugToolkitSchema,
  alvo: SlugToolkitSchema.optional(),
});

/** `toolkit_executar`: pela classe da fase (D21 + D30). Parâmetros em LISTA DE PARES. */
export const ToolkitExecutarEntradaSchema = z.object({
  nome: SlugToolkitSchema,
  alvo: SlugToolkitSchema,
  fase: SlugToolkitSchema,
  parametros: z.array(z.object({ nome: NomeDeParametroSchema, valor: ValorDeParametroSchema })).max(30).default([]),
});
export type ToolkitExecutarEntrada = z.infer<typeof ToolkitExecutarEntradaSchema>;

/** O projeto (máquina, cwd normalizado) de uma rotina ou verbete novo. */
const ProjetoDaPropostaSchema = z.object({ maquinaId: z.string().min(1), cwdProjeto: z.string().min(1).max(1_000) });

/** `toolkit_propor`: só mestres; cria a proposta, e quem decide é a tela (D19). */
export const ToolkitProporEntradaSchema = z.discriminatedUnion('especie', [
  z.object({
    especie: z.literal('versao'),
    rotina: SlugToolkitSchema,
    /** Só para rotina NOVA: título e forma. */
    titulo: z.string().min(1).max(200).optional(),
    forma: z.enum(['FUNCAO', 'CRUD', 'VARIANTE', 'ROTEIRO']).optional(),
    descricao: z.string().min(1).max(LIMITES_TOOLKIT.descricao),
    manifesto: ManifestoRotinaSchema,
    arquivos: z.array(ArquivoPropostoSchema).min(1).max(LIMITES_TOOLKIT.arquivosPorVersao),
    motivo: z.string().min(1).max(LIMITES_TOOLKIT.motivo),
    /** O alvo onde o caminho foi validado ou consertado (R25), se houver. */
    alvoDeOrigem: SlugToolkitSchema.optional(),
    /**
     * Só para rotina NOVA: o projeto dela (ADR-0020), dentro do escopo do
     * criador da mestre. Sem ele a rotina é "da frota" e todo papel a vê —
     * com os nomes de credencial e os endereços junto.
     */
    projeto: ProjetoDaPropostaSchema.optional(),
  }),
  z.object({
    especie: z.literal('extensao'),
    rotina: SlugToolkitSchema,
    alvo: SlugToolkitSchema,
    /** O que se declara igual: SSO, porta, tela, tipos (D31). */
    semelhanca: z.string().min(1).max(2_000),
  }),
  z.object({
    especie: z.literal('verbete'),
    termo: z.string().min(1).max(200),
    classe: z.enum(['PORTA', 'LIMITE', 'ORDEM', 'ROTEIRO', 'RECEITA', 'OUTRO']),
    texto: z.string().min(1).max(LIMITES_TOOLKIT.descricao),
    alvos: z.array(SlugToolkitSchema).max(50).default([]),
    rotinas: z.array(SlugToolkitSchema).max(50).default([]),
    autorDaOrdem: z.string().max(200).optional(),
    dataDaOrdem: z.iso.date().optional(),
    motivo: z.string().min(1).max(LIMITES_TOOLKIT.motivo),
    /** Só para verbete NOVO, com a mesma regra da rotina. */
    projeto: ProjetoDaPropostaSchema.optional(),
  }),
]);
export type ToolkitProporEntrada = z.infer<typeof ToolkitProporEntradaSchema>;

/* ------------------------------------------------------- canal daemon → API */

/**
 * O canal `toolkit.consulta` (daemon → API, com ack), no molde de
 * `mestre.consulta`. Toda consulta carrega a SESSÃO que pediu: a API resolve o
 * escopo por ela — máquina, projeto e criador, RELIDOS a cada chamada
 * (`seguranca.md`) — e confere que a sessão mora na máquina do socket.
 *
 * A sonda (F3.5) roda fora de sessão: só ela registra validação sem
 * `sessaoId`, e aí quem autoriza é a máquina.
 */
export const ToolkitConsultaSchema = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('buscar'), sessaoId: z.string().min(1), ...ToolkitBuscarEntradaSchema.shape }),
  z.object({ tipo: z.literal('ler'), sessaoId: z.string().min(1), ...ToolkitLerEntradaSchema.shape }),
  z.object({
    tipo: z.literal('preparar_execucao'),
    sessaoId: z.string().min(1),
    nome: SlugToolkitSchema,
    alvo: SlugToolkitSchema,
    fase: SlugToolkitSchema,
  }),
  z
    .object({
      tipo: z.literal('registrar_validacao'),
      sessaoId: z.string().min(1).nullable(),
      versaoId: z.string().min(1),
      /** O hash que o daemon CONFERIU antes de rodar — a API recusa se não for o da versão. */
      versaoSha256: z.string().regex(/^[0-9a-f]{64}$/),
      alvo: SlugToolkitSchema,
      fase: SlugToolkitSchema,
      tipoValidacao: z.enum(['ENSAIO', 'REAL', 'SONDA']),
      resultado: ResultadoDeRotinaSchema,
      falhaDe: z.enum(['ALVO', 'AMBIENTE', 'ROTINA']).nullable(),
      motivo: z.string().max(LIMITES_TOOLKIT.motivo),
      /**
       * O recibo (R15): hashes, artefatos com sha256, duração. NUNCA a saída,
       * dado de cliente ou parâmetro secreto — o daemon monta, a API só guarda.
       */
      recibo: z.object({
        duracaoMs: z.number().int().min(0),
        artefatos: z
          .array(z.object({ nome: z.string().max(200), sha256: z.string().regex(/^[0-9a-f]{64}$/), bytes: z.number().int().min(0) }))
          .max(50)
          .default([]),
        fonte: z.object({ descricao: z.string().max(500), em: z.string().max(40), incompleta: z.boolean() }).optional(),
      }),
    })
    .refine((v) => v.sessaoId !== null || v.tipoValidacao === 'SONDA', {
      message: 'so a sonda registra validacao fora de sessao',
      path: ['sessaoId'],
    })
    .refine((v) => (v.resultado === 'FALHOU') === (v.falhaDe !== null), {
      message: 'falhaDe e obrigatorio em FALHOU e proibido fora dele',
      path: ['falhaDe'],
    }),
  z.object({ tipo: z.literal('propor'), sessaoId: z.string().min(1), proposta: ToolkitProporEntradaSchema }),
  /*
    A SONDA (F3.5, D32, R16): roda fora de sessão, pelo laço do daemon, e quem
    autoriza é a MÁQUINA do socket — só as rotinas que ela pode rodar (o
    `onde` e o projeto dela), nos alvos onde a rotina está `validada` ou
    `suspeita`, com a última sonda mais velha que o `sondaACadaHoras`.
  */
  z.object({ tipo: z.literal('sondas_pendentes') }),
  z.object({ tipo: z.literal('preparar_sonda'), nome: SlugToolkitSchema, alvo: SlugToolkitSchema }),
]);
export type ToolkitConsulta = z.infer<typeof ToolkitConsultaSchema>;

/** Uma sonda devida: a rotina e o alvo. O pacote vem pelo `preparar_sonda`. */
export const SondaPendenteSchema = z.object({ rotina: SlugToolkitSchema, alvo: SlugToolkitSchema });
export type SondaPendente = z.infer<typeof SondaPendenteSchema>;

/**
 * O que `preparar_execucao` devolve: a VERSÃO VIGENTE inteira, com o hash de
 * cada arquivo e o da versão, para o daemon CONFERIR antes de rodar (o hash
 * diverge, recusa). E o estado no alvo, que a API já usou para recusar a fase
 * que o estado não permite — o daemon confere de novo (`podeRodar`).
 */
export const PreparoDeExecucaoSchema = z.object({
  rotina: SlugToolkitSchema,
  versaoId: z.string().min(1),
  numero: z.number().int().min(1),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  descricao: z.string(),
  manifesto: ManifestoRotinaSchema,
  arquivos: z
    .array(
      z.object({
        caminho: CaminhoDoPacoteSchema,
        sha256: z.string().regex(/^[0-9a-f]{64}$/),
        bytes: z.number().int().min(0),
        conteudo: z.string(),
      }),
    )
    .min(1)
    .max(LIMITES_TOOLKIT.arquivosPorVersao),
  alvo: SlugToolkitSchema,
  fase: SlugToolkitSchema,
  estadoNoAlvo: z.enum(['sem_versao_aprovada', 'quebrada', 'nao_se_aplica', 'suspeita', 'validada', 'so_ensaio']),
});
export type PreparoDeExecucao = z.infer<typeof PreparoDeExecucaoSchema>;

/** Resposta de qualquer `toolkit.*`, na forma da do mestre. */
export const ToolkitRespostaSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), dados: z.unknown() }),
  z.object({
    ok: z.literal(false),
    erro: z.string().min(1).max(60),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type ToolkitResposta = z.infer<typeof ToolkitRespostaSchema>;
